import React, { useState, useEffect } from "react";

const levels = [
    {
        name: "Livello 1 - Introduzione",
        grid: [
            [1, 1, 1, 0, 0, 0],
            [0, 0, 1, 0, 3, 0],
            [0, 0, 1, 1, 1, 0],
            [0, 0, 0, 0, 1, 0],
            [3, 0, 0, 0, 1, 0],
            [0, 0, 0, 0, 1, 2]
        ],
        maxMoves: 15,
        stars: 2
    },
    {
        name: "Livello 2 - Labirinto",
        grid: [
            [1, 0, 1, 1, 3, 1],
            [1, 0, 1, 0, 0, 1],
            [1, 1, 1, 0, 1, 1],
            [0, 0, 1, 0, 1, 0],
            [3, 1, 1, 1, 1, 0],
            [0, 0, 0, 0, 1, 2]
        ],
        maxMoves: 20,
        stars: 2
    },
    {
        name: "Livello 3 - Difficile",
        grid: [
            [1, 1, 0, 0, 1, 3],
            [0, 1, 1, 0, 1, 0],
            [0, 0, 1, 0, 1, 1],
            [3, 0, 1, 1, 0, 1],
            [0, 0, 0, 1, 0, 1],
            [0, 0, 0, 1, 1, 2]
        ],
        maxMoves: 18,
        stars: 2
    },
    {
        name: "Livello 4 - Master",
        grid: [
            [1, 0, 1, 3, 0, 1],
            [1, 0, 1, 0, 0, 1],
            [1, 1, 1, 1, 1, 1],
            [0, 0, 0, 3, 0, 1],
            [3, 1, 1, 1, 1, 1],
            [0, 0, 0, 0, 0, 2]
        ],
        maxMoves: 22,
        stars: 3
    }
];

export default function PathFinder() {
    const [currentLevel, setCurrentLevel] = useState(0);
    const [path, setPath] = useState([[0,0]]);
    const [starsCollected, setStarsCollected] = useState([]);
    const [moves, setMoves] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [started, setStarted] = useState(false);
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [showHint, setShowHint] = useState(false);

    const level = levels[currentLevel];

    useEffect(() => {
        if (moves > level.maxMoves && !completed && !gameOver) {
            setGameOver(true);
        }
    }, [moves, level.maxMoves, completed, gameOver]);

    const startGame = () => {
        setStarted(true);
        setCurrentLevel(0);
        setPath([[0,0]]);
        setStarsCollected([]);
        setMoves(0);
        setCompleted(false);
        setScore(0);
        setGameOver(false);
        setShowHint(false);
    };

    const handleCellClick = (row, col) => {
        if (gameOver || completed) return;

        const lastPos = path[path.length - 1];

        // Verifica se la cella è adiacente (non diagonale)
        const isAdjacent =
            (Math.abs(row - lastPos[0]) === 1 && col === lastPos[1]) ||
            (Math.abs(col - lastPos[1]) === 1 && row === lastPos[0]);

        if (!isAdjacent) return;

        // Verifica se è già nel percorso (no tornare indietro)
        const alreadyVisited = path.some(([r, c]) => r === row && c === col);
        if (alreadyVisited) return;

        // Verifica se la cella è percorribile (1, 2, o 3)
        if (level.grid[row][col] === 0) return;

        const newPath = [...path, [row, col]];
        setPath(newPath);
        setMoves(moves + 1);

        // Raccogli stella se presente
        if (level.grid[row][col] === 3) {
            setStarsCollected([...starsCollected, `${row}-${col}`]);
        }

        // Verifica se ha raggiunto la fine
        if (level.grid[row][col] === 2) {
            const levelScore = calculateScore();
            setScore(score + levelScore);

            if (currentLevel + 1 < levels.length) {
                setTimeout(() => {
                    setCurrentLevel(currentLevel + 1);
                    setPath([[0,0]]);
                    setStarsCollected([]);
                    setMoves(0);
                    setGameOver(false);
                    setShowHint(false);
                }, 1500);
            } else {
                setCompleted(true);
            }
        }
    };

    const calculateScore = () => {
        const baseScore = 100;
        const starBonus = starsCollected.length * 50;
        const moveBonus = Math.max(0, (level.maxMoves - moves) * 5);
        return baseScore + starBonus + moveBonus;
    };

    const reset = () => {
        setPath([[0,0]]);
        setStarsCollected([]);
        setMoves(0);
        setGameOver(false);
    };

    const skipLevel = () => {
        if (currentLevel + 1 < levels.length) {
            setCurrentLevel(currentLevel + 1);
            setPath([[0,0]]);
            setStarsCollected([]);
            setMoves(0);
            setGameOver(false);
            setShowHint(false);
        } else {
            setCompleted(true);
        }
    };

    const getHint = () => {
        setShowHint(!showHint);
    };

    if (!started) {
        return (
            <div style={{textAlign:"center",padding:"1rem",color:"#e5e7eb",background:"rgba(255,255,255,0.05)",borderRadius:"1rem",boxShadow:"0 0 25px rgba(0,255,200,0.15)"}}>
                <h4 style={{color:"#facc15",marginBottom:"0.5rem"}}>
                    <i className="fas fa-route" style={{marginRight:"6px"}}></i> Path Finder
                </h4>
                <p style={{fontSize:"0.85rem",marginBottom:"0.5rem"}}>
                    Trova il percorso dal punto verde <span style={{color:"#22c55e"}}>●</span> al rosso <span style={{color:"#ef4444"}}>●</span>
                </p>
                <p style={{fontSize:"0.75rem",marginBottom:"1rem",color:"#94a3b8"}}>
                    • Raccogli le stelle <span style={{color:"#facc15"}}>⭐</span><br/>
                    • Mosse limitate per livello<br/>
                    • Non puoi tornare indietro<br/>
                    • 4 livelli progressivi
                </p>
                <button onClick={startGame} style={{background:"linear-gradient(135deg,#facc15,#ca8a04)",border:"none",color:"#000",fontWeight:"700",padding:"0.6rem 1.2rem",borderRadius:"0.5rem",cursor:"pointer"}}>
                    <i className="fas fa-play" style={{marginRight:"6px"}}></i> Inizia
                </button>
            </div>
        );
    }

    if (completed) {
        return (
            <div style={{textAlign:"center",padding:"1.5rem",color:"#e5e7eb",background:"rgba(255,255,255,0.05)",borderRadius:"1rem",boxShadow:"0 0 25px rgba(0,255,200,0.15)"}}>
                <h4 style={{color:"#22c55e",marginBottom:"1rem"}}>
                    <i className="fas fa-trophy" style={{marginRight:"6px"}}></i> Tutti i Livelli Completati!
                </h4>
                <p style={{fontSize:"2rem",fontWeight:"700",color:"#facc15",marginBottom:"0.5rem"}}>
                    {score} punti
                </p>
                <p style={{fontSize:"0.9rem",marginBottom:"1.5rem",color:"#94a3b8"}}>
                    {score >= 600 && "🏆 Percorso Perfetto!"}
                    {score >= 400 && score < 600 && "⭐ Ottimo Navigatore!"}
                    {score < 400 && "🗺️ Esploratore Coraggioso!"}
                </p>
                <button onClick={startGame} style={{background:"linear-gradient(135deg,#16a34a,#065f46)",border:"none",color:"#fff",fontWeight:"700",padding:"0.6rem 1.2rem",borderRadius:"0.5rem",cursor:"pointer"}}>
                    <i className="fas fa-redo" style={{marginRight:"6px"}}></i> Rigioca
                </button>
            </div>
        );
    }

    if (gameOver) {
        return (
            <div style={{textAlign:"center",padding:"1.5rem",color:"#e5e7eb",background:"rgba(255,255,255,0.05)",borderRadius:"1rem",boxShadow:"0 0 25px rgba(0,255,200,0.15)"}}>
                <h4 style={{color:"#ef4444",marginBottom:"1rem"}}>
                    <i className="fas fa-times-circle" style={{marginRight:"6px"}}></i> Mosse Esaurite!
                </h4>
                <p style={{fontSize:"1.2rem",marginBottom:"1.5rem",color:"#94a3b8"}}>
                    Hai superato il limite di {level.maxMoves} mosse
                </p>
                <div style={{display:"flex",gap:"0.5rem",justifyContent:"center"}}>
                    <button onClick={reset} style={{background:"linear-gradient(135deg,#2563eb,#1e40af)",border:"none",color:"#fff",fontWeight:"700",padding:"0.6rem 1rem",borderRadius:"0.5rem",cursor:"pointer",fontSize:"0.85rem"}}>
                        <i className="fas fa-undo" style={{marginRight:"6px"}}></i> Riprova
                    </button>
                    <button onClick={skipLevel} style={{background:"rgba(239,68,68,0.2)",border:"1px solid rgba(239,68,68,0.3)",color:"#ef4444",fontWeight:"700",padding:"0.6rem 1rem",borderRadius:"0.5rem",cursor:"pointer",fontSize:"0.85rem"}}>
                        <i className="fas fa-forward" style={{marginRight:"6px"}}></i> Salta
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{textAlign:"center",padding:"1.5rem",color:"#e5e7eb",background:"rgba(255,255,255,0.05)",borderRadius:"1rem",boxShadow:"0 0 25px rgba(0,255,200,0.15)"}}>
            <div style={{marginBottom:"0.5rem",color:"#facc15",fontSize:"0.9rem",fontWeight:"700"}}>
                {level.name}
            </div>

            <div style={{display:"flex",justifyContent:"space-between",marginBottom:"1rem",fontSize:"0.75rem",color:"#94a3b8"}}>
                <span>
                    🎯 Livello {currentLevel + 1}/{levels.length}
                </span>
                <span>
                    👣 {moves}/{level.maxMoves}
                </span>
                <span>
                    ⭐ {starsCollected.length}/{level.stars}
                </span>
            </div>

            {showHint && (
                <div style={{padding:"0.5rem",background:"rgba(59,130,246,0.1)",borderRadius:"0.5rem",marginBottom:"1rem",fontSize:"0.7rem",color:"#94a3b8",border:"1px solid rgba(59,130,246,0.3)"}}>
                    💡 Cerca di raccogliere tutte le stelle nel minor numero di mosse possibile!
                </div>
            )}

            <div style={{
                display:"grid",
                gridTemplateColumns:"repeat(6, 1fr)",
                gap:"2px",
                maxWidth:"240px",
                margin:"0 auto 1rem",
                background:"rgba(0,0,0,0.3)",
                padding:"3px",
                borderRadius:"0.5rem"
            }}>
                {level.grid.map((row, i) =>
                    row.map((cell, j) => {
                        const isInPath = path.some(([r,c]) => r === i && c === j);
                        const isStart = i === 0 && j === 0;
                        const isEnd = cell === 2;
                        const isStar = cell === 3;
                        const isCollected = starsCollected.includes(`${i}-${j}`);
                        const lastPos = path[path.length - 1];
                        const isAdjacent =
                            (Math.abs(i - lastPos[0]) === 1 && j === lastPos[1]) ||
                            (Math.abs(j - lastPos[1]) === 1 && i === lastPos[0]);

                        return (
                            <div
                                key={`${i}-${j}`}
                                onClick={() => handleCellClick(i, j)}
                                style={{
                                    aspectRatio:"1",
                                    background:
                                        cell === 0 ? "#1e293b" :
                                            isStart ? "#22c55e" :
                                                isEnd ? "#ef4444" :
                                                    isInPath ? "#3b82f6" :
                                                        isAdjacent && cell !== 0 ? "rgba(250,204,21,0.3)" :
                                                            "#475569",
                                    cursor: cell !== 0 && isAdjacent && !isInPath ? "pointer" : "default",
                                    borderRadius:"3px",
                                    transition:"all 0.2s ease",
                                    display:"flex",
                                    alignItems:"center",
                                    justifyContent:"center",
                                    fontSize:"0.8rem",
                                    border: isAdjacent && cell !== 0 && !isInPath ? "2px solid #facc15" : "none",
                                    position:"relative"
                                }}
                            >
                                {isStart && <span style={{fontSize:"0.9rem"}}>🎯</span>}
                                {isEnd && <span style={{fontSize:"0.9rem"}}>🏁</span>}
                                {isStar && !isCollected && <span style={{fontSize:"0.8rem"}}>⭐</span>}
                                {isInPath && !isStart && !isEnd && !isStar && (
                                    <span style={{color:"#fff",fontSize:"0.6rem",fontWeight:"700"}}>
                                        {path.findIndex(([r,c]) => r === i && c === j)}
                                    </span>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            <div style={{fontSize:"0.7rem",color:"#94a3b8",marginBottom:"1rem"}}>
                Le celle gialle sono raggiungibili • I numeri mostrano l'ordine del percorso
            </div>

            <div style={{display:"flex",gap:"0.5rem",flexWrap:"wrap",justifyContent:"center"}}>
                <button onClick={reset} style={{flex:"1",minWidth:"70px",background:"rgba(239,68,68,0.2)",border:"1px solid rgba(239,68,68,0.3)",color:"#ef4444",fontWeight:"700",padding:"0.5rem 0.8rem",borderRadius:"0.5rem",cursor:"pointer",fontSize:"0.75rem"}}>
                    <i className="fas fa-undo" style={{marginRight:"4px"}}></i> Reset
                </button>
                <button onClick={getHint} style={{flex:"1",minWidth:"70px",background:"rgba(59,130,246,0.2)",border:"1px solid rgba(59,130,246,0.3)",color:"#3b82f6",fontWeight:"700",padding:"0.5rem 0.8rem",borderRadius:"0.5rem",cursor:"pointer",fontSize:"0.75rem"}}>
                    <i className="fas fa-lightbulb" style={{marginRight:"4px"}}></i> Hint
                </button>
                <button onClick={skipLevel} style={{flex:"1",minWidth:"70px",background:"rgba(148,163,184,0.2)",border:"1px solid rgba(148,163,184,0.3)",color:"#94a3b8",fontWeight:"700",padding:"0.5rem 0.8rem",borderRadius:"0.5rem",cursor:"pointer",fontSize:"0.75rem"}}>
                    <i className="fas fa-forward" style={{marginRight:"4px"}}></i> Salta
                </button>
            </div>

            <div style={{marginTop:"1rem",padding:"0.6rem",background:"rgba(34,197,94,0.1)",borderRadius:"0.5rem",fontSize:"0.75rem",color:"#94a3b8",border:"1px solid rgba(34,197,94,0.2)"}}>
                💰 Punteggio attuale: <strong style={{color:"#22c55e"}}>{score}</strong>
            </div>
        </div>
    );
}